import {
  useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, baixarBlob, enviarFormulario, ErroApi } from '../../api';
import { data, horaCurta, nomeDoContato, numero, plural, telefoneDigitadoLegivel, telefoneLegivel } from '../../formato';
import { useSessao } from '../../sessao';
import { degrauDe, ENTREGA_DA_MENSAGEM, SITUACAO_DO_ATENDIMENTO } from '../../situacoes';
import { AtualizacaoPausada, useTempoReal } from '../../tempoReal';
import {
  Aviso, Carregando, ErroDaApi, FotoProtegida, LinkProtegido, RespostaDaAcao, Selo, SeloDaEscada, ZonaDeArquivo,
} from '../../ui';
import { Icone } from '../../Icone';
import { MenuDaOs } from '../OsMoldura';
import { GRAVAR_CONTATO, JanelaDoContato } from './JanelaDoContato';
import { FotoDoContato } from './FotoDoContato';
import {
  chamarOContato, JanelaDaLigacao, JanelaDeDevolver, JanelaDeEncerrar, JanelaDeTransferir, JanelaDoLocalDaObra,
  type LocalDaObra,
} from './JanelasDaConversa';
import { JanelaNovaConversa, type InicialDaNovaConversa } from './JanelaNovaConversa';
import {
  CartaoDeContato, FigurinhaDaMensagem, LocalizacaoDaMensagem, VideoDaMensagem,
  type CartaoDaLinha, type LocalizacaoDaLinha,
} from './MidiasDaMensagem';
import { SugestaoDoAgente, type SugestaoDaConversa } from './SugestaoDoAgente';

/**
 * A conversa aberta — onda 550 (Plano 2 · Comercial, Etapa 1, Tarefa 14;
 * spec do Comercial §8.1, «Conversa»).
 *
 * ─── O que ela mostra ─────────────────────────────────────────────────
 *
 * A linha do tempo inteira, na ordem em que aconteceu: o que o cliente
 * escreveu (à esquerda), o que nós respondemos (à direita, com a entrega:
 * «Na fila», «Enviada», «Entregue», «Lida», ou «Não entregue» com «Tentar
 * de novo»), o áudio com a transcrição embaixo do player, o anexo pelo
 * `LinkProtegido` (a brecha do `blob:` fechada na Tarefa 13 continua
 * fechada: o HTML que um cliente mande desce como arquivo), a ligação como
 * cartão e a nota interna em outro tom — o cliente nunca a vê.
 *
 * ─── Ao vivo ──────────────────────────────────────────────────────────
 *
 * `useTempoReal(['comercial.conversa.<id>'])`: cada aviso (mensagem nova,
 * a entrega que mudou, a conversa que mudou de dono) é «mudou, vá buscar» —
 * a conversa busca de novo pela rota de sempre, que confere a permissão. O
 * `{ recarregar: true }` é tratado do mesmo jeito: busca tudo de novo.
 *
 * ─── As ações ─────────────────────────────────────────────────────────
 *
 * Assumir (sem janela), Transferir, Registrar ligação, Devolver ao agente
 * e Encerrar (nas janelas da `JanelasDaConversa`). A ação primária da tela
 * é «Enviar», no compositor — a única (P-UI-10). A resposta de cada ação
 * nasce onde a pessoa agiu (P-UI-26), logo abaixo do cabeçalho, onde estão as ações; a
 * recusa da API é a frase da casa («Bruno já assumiu esta conversa.»).
 */

// ── O que a API devolve (Tarefa 13) ──────────────────────────────────

export interface ItemDaCaixa {
  id: string;
  /** `temFoto` (onda 555): a foto do perfil do WhatsApp, pela rota do contato; sem ela, as iniciais. */
  contato: { id: string; nome: string; temFoto?: boolean };
  canal: { id: string; codigo: string; nome: string; tipo: string; agenteAtivo?: boolean };
  rota: { codigo: string; nome: string } | null;
  dono: { tipo: 'pessoa' | 'agente' | 'ninguem'; id: string | null; nome: string | null };
  situacao: 'aberta' | 'aguardando_cliente' | 'encerrada';
  prioridade: boolean;
  tipo: 'cliente' | 'instrucao';
  assumidaEm: string | null;
  /** `oculta`: a última é comercial e a pessoa não tem o Comercial — sem texto nem tipo (Tarefa 16b). */
  ultimaMensagem: { texto: string | null; direcao: string; tipo: string | null; criadaEm: string; oculta?: boolean } | null;
  minutosSemResposta: number | null;
  prazoSubstituto: string | null;
  substituto: { id: string; nome: string } | null;
}

export interface MensagemDaLinha {
  id: string;
  direcao: 'recebida' | 'enviada' | 'interna';
  autorTipo: 'cliente' | 'pessoa' | 'agente' | 'sistema';
  autor: { id: string; nome: string } | null;
  /** Vazio quando a mensagem está oculta. */
  tipo: string | null;
  texto: string | null;
  arquivo: { id: string; nome: string; mime: string; tamanho: number } | null;
  ligacao: { direcao: 'recebida' | 'feita'; duracaoSegundos: number } | null;
  criadaEm: string;
  peloAparelho: boolean;
  /** `motivo`: só na resposta do agente que a fila recusou na hora de sair (0513 · RN-478). */
  entrega?: {
    saidaId: string; situacao: string; tentativas: number; canal: string; motivo?: string | null;
    /** O destino da saída que falhou porque o número não tem WhatsApp (Parte B, Tarefa 2): «Não enviada…». */
    semWhatsapp?: string | null;
  };
  transcricao?: { situacao: string; texto: string | null };
  /** Marcada «comercial» (0528, Tarefa 16b). */
  comercial?: boolean;
  /** Comercial, e a pessoa não tem o Comercial: só o lugar (quem, quando, a entrega). */
  oculta?: boolean;
  /** O anexo está numa mensagem comercial (esta ou outra): não vem, nem o nome. */
  arquivoOculto?: boolean;
  /** Veio da importação do histórico do WhatsApp (onda 553). */
  historico?: boolean;
  /** O WhatsApp já não tinha a mídia na importação: só o nome e o tipo. */
  /** `motivo: 'grande'`: passou de 25 MB, e a importação não a trouxe. */
  midiaIndisponivel?: { nome: string | null; mime: string | null; motivo?: 'grande' } | null;
  /** Onda 555 (A-1): a localização (tipo `localizacao`), o cartão (tipo `contato`) e a mídia que não veio ao vivo. */
  localizacao?: LocalizacaoDaLinha | null;
  cartao?: CartaoDaLinha | null;
  /** `grande`: passou de 25 MB e ficou no celular; `expirada`: o WhatsApp não a entregou. Sem arquivo. */
  midiaAusente?: 'grande' | 'expirada' | null;
}

export interface DetalheDaConversa {
  conversa: ItemDaCaixa & {
    resumo: unknown; agenteSilencio: boolean; emailAssunto: string | null;
    abertaEm: string; encerradaEm: string | null; naoReceberEm: string | null; origem?: string;
    /** A conversa aberta pela equipe (Parte B, §3.2): a primeira mensagem de quem a abriu leva «iniciou a conversa». */
    iniciadaPor?: { id: string; nome: string } | null;
    iniciadaEm?: string | null;
  };
  linhaDoTempo: MensagemDaLinha[];
  /** O que o central tinha antes da conexão, deste contato; `ate` é o corte da importação. */
  historico?: { ate: string; mensagens: MensagemDaLinha[] } | null;
  /** O corte da importação, quando a conversa mostra algo do histórico: a divisória leva esta data. */
  corteDoHistorico?: string | null;
  contato: {
    id: string; nome: string; origem: string; email: string | null; telefone: string | null;
    whatsapp: string | null; cargo: string | null; naoReceberEm: string | null;
    /** Falso para quem chegou sozinho (pelo WhatsApp, pela ligação, por número novo): «Não gravado». */
    salvo?: boolean;
    /** O nome do perfil do WhatsApp («Carla M.»), em cinza ao lado do número. */
    whatsappPerfil?: string | null;
    /** O perfil do WhatsApp (onda 555, §3.1): a foto (pela rota do contato), o recado e a conta comercial. */
    temFoto?: boolean;
    recado?: string | null;
    /** Só os textos que o WhatsApp deu (categoria, descrição, site, horário, endereço, e-mail); nula sem conta comercial. */
    contaComercial?: Record<string, string> | null;
  };
  clientes: { id: string; nome: string; razaoSocial: string; papel: string | null; estrategico: boolean }[];
  oportunidade: {
    id: string; estagio: string; estagioDesde: string | null; linha: string | null;
    local: { endereco: string | null; municipio: string | null; uf: string | null };
    escopo: string | null; furos: number | null; areaM2: number | null; origem: string;
    responsavel: { id: string; nome: string } | null; proximaAcao: string | null; proximaAcaoEm: string | null;
    valorEstimado?: number | null; valorPelaRegra?: boolean;
  } | null;
  /** Quem tem o Comercial (crm.ver) vê a seção da oportunidade (Tarefa 15). */
  veOportunidade?: boolean;
  /** Quem tem o Comercial vê o chip «Comercial» e marca ou desmarca a mensagem (Tarefa 16b). */
  veComercial?: boolean;
  tarefas: {
    id: string; tipo: string; titulo: string | null; venceEm: string; situacao: string;
    responsavel: { tipo: 'pessoa' | 'agente'; id: string | null; nome: string | null };
  }[];
  ultimaOs: { id: string; numero: string; statusTecnico: string; criadoEm: string } | null;
  /** A sugestão do agente que vale agora, acima do compositor (Tarefa 24). */
  sugestao?: SugestaoDaConversa | null;
}

// ── O relógio da conversa, escrito como se fala ──────────────────────

/** A primeira palavra do nome: na lista, «passa a Fabio», e não o nome inteiro. */
export const primeiroNome = (nome: string | null | undefined) => (nome ?? '').trim().split(/\s+/)[0] ?? '';

/** «sem resposta há 7 min», «há 1 h 20», «há 2 h», «há 3 dias». */
export function semRespostaHa(minutos: number | null): string | null {
  if (minutos === null || minutos < 0) return null;
  const m = Math.floor(minutos);
  if (m < 1) return 'sem resposta há menos de 1 min';
  if (m < 60) return `sem resposta há ${m} min`;
  if (m < 1440) {
    const h = Math.floor(m / 60);
    const r = m % 60;
    return r ? `sem resposta há ${h} h ${String(r).padStart(2, '0')}` : `sem resposta há ${h} h`;
  }
  return `sem resposta há ${plural(Math.floor(m / 1440), 'dia', 'dias')}`;
}

/** «passa a Fabio às 14:32» (hoje), «passa a Fabio em 03/11 às 08:20», «passa a Fabio agora». */
export function passaA(substituto: { nome: string } | null, prazo: string | null, agora = new Date()): string | null {
  if (!substituto || !prazo) return null;
  const quem = primeiroNome(substituto.nome);
  const p = new Date(prazo);
  if (p.getTime() <= agora.getTime()) return `passa a ${quem} agora`;
  return data(p) === data(agora) ? `passa a ${quem} às ${horaCurta(p)}` : `passa a ${quem} em ${data(p).slice(0, 5)} às ${horaCurta(p)}`;
}

/** De quem é a vez: a situação do atendimento (`SITUACAO_DO_ATENDIMENTO`). */
export function situacaoDoAtendimento(c: Pick<ItemDaCaixa, 'situacao' | 'dono' | 'minutosSemResposta'>): string {
  if (c.situacao === 'encerrada') return 'encerrada';
  if (c.situacao === 'aguardando_cliente') return 'aguardando_cliente';
  if (c.dono.tipo === 'agente') return 'com_agente';
  return c.minutosSemResposta !== null ? 'sem_resposta' : 'aberta';
}

/** Quem está com a conversa, numa frase curta. */
export function comQuem(c: Pick<ItemDaCaixa, 'dono' | 'assumidaEm'>): string {
  if (c.dono.tipo === 'agente') return 'Com o agente';
  if (c.dono.tipo !== 'pessoa' || !c.dono.nome) return 'Sem dono';
  return c.assumidaEm ? `Com ${c.dono.nome}` : `Com ${c.dono.nome} · ainda não assumiu`;
}

/** No meio da frase do cabeçalho: «… · com Humberto», «… · sem dono». */
const minuscula = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);

/** Minutos e segundos: «4 min», «4 min 30 s», «45 s». */
function duracao(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (!m) return `${r} s`;
  return r ? `${m} min ${r} s` : `${m} min`;
}

export const tamanho = (b: number) => (b < 1024 * 1024 ? `${numero(b / 1024, 0)} kB` : `${numero(b / 1024 / 1024, 1)} MB`);

// ── As peças da linha do tempo ───────────────────────────────────────

/**
 * O áudio: os bytes pela sessão (`baixarBlob`, que guarda o tipo só do que
 * a lista deixa — áudio deixa) num endereço local para o player. O
 * endereço é solto quando a mensagem sai da tela.
 */
function AudioProtegido({ arquivo }: { arquivo: NonNullable<MensagemDaLinha['arquivo']> }) {
  const [src, setSrc] = useState<string | null>(null);
  const [falhou, setFalhou] = useState(false);
  useEffect(() => {
    const abortar = new AbortController();
    let endereco: string | null = null;
    baixarBlob(`/arquivos/${arquivo.id}/conteudo`, abortar.signal)
      .then((b) => { endereco = URL.createObjectURL(b); setSrc(endereco); })
      .catch((e) => { if ((e as Error).name !== 'AbortError') setFalhou(true); });
    return () => { abortar.abort(); if (endereco) URL.revokeObjectURL(endereco); };
  }, [arquivo.id]);
  return (
    <div className="msg-audio">
      {src ? <audio controls preload="metadata" src={src} aria-label="Áudio do cliente" />
        : falhou ? (
          <span className="msg-aux">
            O áudio não abriu aqui.{' '}
            <LinkProtegido caminho={`/arquivos/${arquivo.id}/conteudo`} nomeQueda={arquivo.nome}>Baixe o arquivo</LinkProtegido>.
          </span>
        ) : <Carregando texto="Carregando o áudio" />}
    </div>
  );
}

/** Quem enviou: o agente, a pessoa, ou o aparelho — no histórico, a equipe pelo celular do central. */
function autorDaEnviada(m: MensagemDaLinha): string {
  if (m.autorTipo === 'agente') return 'Agente';
  if (m.autor?.nome) return m.autor.nome;
  if (m.peloAparelho) return m.historico ? 'Equipe (pelo celular do central)' : 'Pelo aparelho';
  return 'Testesolo';
}

/** O áudio do histórico chegou sem transcrição: o botão pede, e a frase confirma. */
function Transcricao({ m }: { m: MensagemDaLinha }) {
  const [pedida, setPedida] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [pedindo, setPedindo] = useState(false);
  const pedir = async () => {
    if (pedindo) return;
    setPedindo(true);
    setErro(null);
    try {
      await api(`/comercial/mensagens/${m.id}/transcrever`, { metodo: 'POST' });
      setPedida(true);
    } catch (e) { setErro(e); } finally { setPedindo(false); }
  };
  const situacao = m.transcricao?.situacao;
  if (situacao === 'pronta' && m.transcricao?.texto) {
    return <p className="transcricao"><span className="transcricao-rotulo">Transcrição</span>{m.transcricao.texto}</p>;
  }
  if (situacao === 'falhou') return <p className="transcricao">A transcrição não saiu. Ouça o áudio.</p>;
  if (m.transcricao) return <p className="transcricao">{pedida ? 'Transcrição pedida' : 'Transcrevendo o áudio…'}</p>;
  if (pedida) return <p className="transcricao">Transcrição pedida</p>;
  if (!m.historico) return <p className="transcricao">Sem transcrição.</p>;
  return (
    <div className="transcricao">
      <button type="button" className="botao secundario" disabled={pedindo} onClick={() => void pedir()}>Transcrever</button>
      {erro != null && <ErroDaApi erro={erro} />}
    </div>
  );
}

function Anexo({ m }: { m: MensagemDaLinha }) {
  const a = m.arquivo!;
  if (m.tipo === 'audio' || a.mime.startsWith('audio/')) {
    return (
      <>
        <AudioProtegido arquivo={a} />
        <Transcricao m={m} />
      </>
    );
  }
  /* Onda 555: o vídeo com o player e a duração; a figurinha mora fora do balão (na `Mensagem`). */
  if (m.tipo === 'video' || a.mime.startsWith('video/')) return <VideoDaMensagem arquivo={a} />;
  if (m.tipo === 'figurinha') return <FigurinhaDaMensagem arquivo={a} />;
  if (a.mime.startsWith('image/')) {
    return (
      <LinkProtegido caminho={`/arquivos/${a.id}/conteudo`} nomeQueda={a.nome} className="msg-imagem">
        <FotoProtegida arquivoId={a.id} alt={a.nome}
          carregando={<Carregando texto="Carregando a imagem" />}
          semImagem={<span className="msg-aux">{a.nome}</span>} />
      </LinkProtegido>
    );
  }
  return (
    <LinkProtegido caminho={`/arquivos/${a.id}/conteudo`} nomeQueda={a.nome} className="msg-arquivo">
      <span className="msg-arquivo-nome">{a.nome}</span>
      <span className="msg-aux">{tamanho(a.tamanho)}</span>
    </LinkProtegido>
  );
}

/** O nome da mídia que não veio, pelo tipo da mensagem. */
const MIDIA_DO_TIPO: { readonly [t: string]: string } = {
  video: 'Vídeo', imagem: 'Imagem', audio: 'Áudio', figurinha: 'Figurinha', arquivo: 'Arquivo',
};

/**
 * A mídia que não está aqui — uma marca só (onda 555, decisão do controle):
 * a do histórico (`midiaIndisponivel`, onda 553, como era) ou a que não veio
 * ao vivo (`midiaAusente`): a grande ficou no celular, a expirada o WhatsApp
 * não entregou.
 */
function MidiaQueNaoVeio({ m }: { m: MensagemDaLinha }) {
  let texto: string | null = null;
  if (m.midiaIndisponivel) {
    texto = m.midiaIndisponivel.motivo === 'grande'
      ? 'mídia acima de 25 MB (não importada)'
      : 'mídia não disponível (antes da importação)';
  } else if (m.midiaAusente === 'grande') {
    texto = `${MIDIA_DO_TIPO[m.tipo ?? ''] ?? 'Mídia'} grande demais para guardar: veja no celular`;
  } else if (m.midiaAusente === 'expirada') {
    texto = 'Mídia não recebida: peça de novo ao cliente';
  }
  if (!texto) return null;
  return (
    <p className="msg-midia-indisponivel">
      {texto}
      {m.midiaIndisponivel?.nome ? <span className="msg-aux"> · {m.midiaIndisponivel.nome}</span> : null}
    </p>
  );
}

/** O que a conversa oferece nas mídias novas (onda 555): o local da obra e o cartão de contato. */
export interface AcoesDaMidia {
  /** «Usar como local da obra»: a localização recebida desta conversa, com oportunidade, para quem a edita. */
  aoUsarComoLocal?(mensagemId: string): void;
  /** A mensagem cuja localização está sendo gravada. */
  usandoLocal?: string | null;
  aoGravarContato?(c: CartaoDaLinha): void;
  aoNovaConversa?(c: CartaoDaLinha): void;
  aoAbrirContato?(contatoId: string): void;
}

function Entrega({ m, aoTentar, aoRegistrarLigacao }: {
  m: MensagemDaLinha; aoTentar(saidaId: string): void;
  /** O atalho da mensagem que não saiu porque o número não tem WhatsApp (sem ele, a frase só). */
  aoRegistrarLigacao?(): void;
}) {
  if (!m.entrega) {
    return m.peloAparelho ? <span className="msg-aux">Enviada pelo aparelho</span> : null;
  }
  /* O número sem WhatsApp (spec §3.2, quadro 2): tentar de novo não adianta — a ligação, sim. */
  if (m.entrega.semWhatsapp) {
    return (
      <span className="msg-entrega msg-nao-enviada">
        <span className="msg-nao-enviada-texto">
          Não enviada: o número {telefoneLegivel(m.entrega.semWhatsapp)} não tem WhatsApp.
        </span>
        {aoRegistrarLigacao && (
          <button type="button" className="botao discreto msg-tentar" onClick={aoRegistrarLigacao}>Registrar ligação</button>
        )}
      </span>
    );
  }
  const d = degrauDe(ENTREGA_DA_MENSAGEM, m.entrega.situacao);
  return (
    <span className="msg-entrega">
      <SeloDaEscada degrau={d} />
      {/* A resposta do agente que não saiu não volta à fila (0513): quem
          responde agora é a pessoa, pela caixa. */}
      {m.entrega.situacao === 'falhou' && m.autorTipo !== 'agente' && (
        <button type="button" className="botao discreto msg-tentar" onClick={() => aoTentar(m.entrega!.saidaId)}>
          Tentar de novo
        </button>
      )}
      {m.entrega.situacao === 'falhou' && m.autorTipo === 'agente' && (
        <span className="msg-aux msg-motivo">
          {m.entrega.motivo ? `${m.entrega.motivo} ` : ''}Se ela ainda servir, responda pela caixa de entrada.
        </span>
      )}
    </span>
  );
}

/**
 * A marca «comercial» de uma mensagem (Tarefa 16b, decisão do Humberto de
 * 23/10): para quem tem o Comercial, o chip e o menu «⋯» da mensagem com
 * marcar ou desmarcar. Quem não tem, não vê nenhum dos dois.
 */
function MarcaComercial({ m, aoMarcar }: { m: MensagemDaLinha; aoMarcar?(id: string, comercial: boolean): void }) {
  if (!aoMarcar) return null;
  return (
    <>
      {m.comercial && <span className="msg-comercial"><Selo tom="atencao">Comercial</Selo></span>}
      <MenuDaOs rotulo="Mais ações da mensagem">
        <button type="button" className="item-menu" onClick={() => aoMarcar(m.id, !m.comercial)}>
          {m.comercial ? 'Desmarcar como comercial' : 'Marcar como comercial'}
        </button>
      </MenuDaOs>
    </>
  );
}

function Mensagem({ m, contato, aoTentar, aoMarcar, iniciou = false, aoRegistrarLigacao, chamar, acoes = {} }: {
  m: MensagemDaLinha; contato: string; aoTentar(saidaId: string): void;
  /** As ações das mídias novas (onda 555). */
  acoes?: AcoesDaMidia;
  /** Só para quem tem o Comercial. */
  aoMarcar?(id: string, comercial: boolean): void;
  /** A primeira mensagem da conversa aberta pela equipe (§3.2). */
  iniciou?: boolean;
  aoRegistrarLigacao?(): void;
  /** Na ligação registrada (§3.4): o número e «Chamar no WhatsApp». */
  chamar?: { numero: string | null; aoChamar(): void };
}) {
  const quando = <time dateTime={m.criadaEm}>{horaCurta(m.criadaEm)}</time>;
  const marca = <MarcaComercial m={m} aoMarcar={aoMarcar} />;
  if (m.oculta) {
    /* O lugar da mensagem comercial, para quem não tem o Comercial: quem,
       quando, de que lado e a entrega — nada do conteúdo (a API nem o manda). */
    const enviada = m.direcao === 'enviada';
    const autor = m.direcao === 'interna' ? (m.autor?.nome ?? 'Nota')
      : enviada ? autorDaEnviada(m) : contato;
    return (
      <li className={`msg oculta ${enviada ? 'enviada' : 'recebida'}`} data-mensagem={m.id}>
        <div className="msg-balao">
          <div className="msg-cabeca">
            <strong>{autor}</strong>
            {quando}
          </div>
          <p className="msg-oculta-texto">Mensagem comercial oculta</p>
          <span className="msg-aux">O conteúdo é de quem tem o Comercial.</span>
          {enviada && <Entrega m={m} aoTentar={aoTentar} />}
        </div>
      </li>
    );
  }
  if (m.tipo === 'ligacao' && m.ligacao) {
    return (
      <li className="msg ligacao" data-mensagem={m.id}>
        <div className="msg-cabeca">
          <strong>{m.ligacao.direcao === 'recebida' ? 'O cliente ligou' : 'Nós ligamos'}</strong>
          <span className="msg-aux">{duracao(m.ligacao.duracaoSegundos)}{m.autor ? ` · ${m.autor.nome}` : ''}</span>
          {quando}
          {marca}
        </div>
        {m.texto && <p className="msg-texto">{m.texto}</p>}
        {chamar && (
          <div className="msg-ligacao-chamar">
            {chamar.numero && <span className="msg-aux mono">{chamar.numero}</span>}
            <button type="button" className="botao secundario" onClick={chamar.aoChamar}>Chamar no WhatsApp</button>
          </div>
        )}
      </li>
    );
  }
  if (m.direcao === 'interna') {
    const sistema = m.autorTipo === 'sistema';
    return (
      <li className={`msg interna${sistema ? ' do-sistema' : ''}`} data-mensagem={m.id}>
        <div className="msg-cabeca">
          <strong>{sistema ? 'Nota do sistema' : 'Nota interna'}</strong>
          {m.autor && <span className="msg-aux">{m.autor.nome}</span>}
          {quando}
          {marca}
        </div>
        {m.texto && <p className="msg-texto">{m.texto}</p>}
        <span className="msg-aux">O cliente não vê esta nota.</span>
      </li>
    );
  }
  const enviada = m.direcao === 'enviada';
  const autor = enviada ? autorDaEnviada(m) : contato;
  /* A figurinha aparece como imagem, sem balão (§3.1): quem e quando, miúdos, embaixo dela. */
  if (m.tipo === 'figurinha' && m.arquivo) {
    return (
      <li className={`msg ${enviada ? 'enviada' : 'recebida'} figurinha`} data-mensagem={m.id}>
        <div className="msg-figurinha">
          <FigurinhaDaMensagem arquivo={m.arquivo} />
          <div className="msg-cabeca">
            <strong>{autor}</strong>
            {quando}
            {marca}
          </div>
          {enviada && <Entrega m={m} aoTentar={aoTentar} />}
        </div>
      </li>
    );
  }
  const local = m.tipo === 'localizacao' ? m.localizacao ?? null : null;
  const cartao = m.tipo === 'contato' ? m.cartao ?? null : null;
  return (
    <li className={`msg ${enviada ? 'enviada' : 'recebida'}${m.arquivo || local || cartao ? ` ${m.tipo}` : ''}${
      m.entrega?.semWhatsapp ? ' nao-enviada' : ''}`} data-mensagem={m.id}>
      <div className="msg-balao">
        <div className="msg-cabeca">
          <strong>{autor}</strong>
          {iniciou && <span className="msg-aux msg-iniciou">iniciou a conversa</span>}
          {quando}
          {marca}
        </div>
        {m.arquivo && <Anexo m={m} />}
        <MidiaQueNaoVeio m={m} />
        {m.arquivoOculto && <p className="msg-oculta-texto">Anexo comercial oculto</p>}
        {local && (
          <LocalizacaoDaMensagem local={local} usando={acoes.usandoLocal === m.id}
            aoUsar={!enviada && acoes.aoUsarComoLocal ? () => acoes.aoUsarComoLocal!(m.id) : undefined} />
        )}
        {cartao && (
          <CartaoDeContato cartao={cartao} legenda={m.texto}
            aoGravar={acoes.aoGravarContato ? () => acoes.aoGravarContato!(cartao) : undefined}
            aoNovaConversa={acoes.aoNovaConversa ? () => acoes.aoNovaConversa!(cartao) : undefined}
            aoAbrir={cartao.contatoId && acoes.aoAbrirContato ? () => acoes.aoAbrirContato!(cartao.contatoId!) : undefined} />
        )}
        {m.texto && !cartao && <p className="msg-texto">{m.texto}</p>}
        {enviada && <Entrega m={m} aoTentar={aoTentar} aoRegistrarLigacao={aoRegistrarLigacao} />}
      </div>
    </li>
  );
}

/** O dia, como cabeça do grupo: «Hoje», «Ontem» ou a data. */
function rotuloDoDia(iso: string): string {
  const d = data(iso);
  const hoje = new Date();
  if (d === data(hoje)) return 'Hoje';
  if (d === data(new Date(hoje.getTime() - 86_400_000))) return 'Ontem';
  return d;
}

/** As mensagens em grupos de dia, na ordem em que vêm. */
function agruparPorDia(lista: MensagemDaLinha[]) {
  const g: { dia: string; itens: MensagemDaLinha[] }[] = [];
  for (const m of lista) {
    const dia = rotuloDoDia(m.criadaEm);
    if (g.at(-1)?.dia === dia) g.at(-1)!.itens.push(m);
    else g.push({ dia, itens: [m] });
  }
  return g;
}

// ── O compositor ─────────────────────────────────────────────────────

export interface RespostaPronta { id: string; titulo: string; texto: string; atalho: string | null }

let respostasProntas: Promise<RespostaPronta[]> | null = null;
/** As respostas prontas, buscadas uma vez por aba (o compositor e a Nova conversa). */
export const buscarRespostas = () => (respostasProntas ??= api<RespostaPronta[]>('/comercial/respostas-prontas')
  .catch((e) => { respostasProntas = null; throw e; }));

/** Sem acento e em minúsculas, para o «/» achar «saudação» digitando «saudacao». */
export const semAcento = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Escrever, anexar e enviar. O «/» no começo abre as respostas prontas
 * (filtra pelo atalho e pelo título; ↑ ↓ escolhem, Enter põe o texto no
 * compositor, Esc fecha) — o texto continua editável antes de enviar.
 * Ctrl+Enter envia. O anexo sobe ao escolher (até 25 MB), e vai com a
 * resposta.
 *
 * O compositor compacto (P-LST-15, SP-a · A de 23/10/2026): UMA fileira —
 * o clipe (botão só de ícone, com o nome no `aria-label`), o campo e o
 * «Enviar». O campo nasce com uma linha e cresce com o texto até ~6
 * linhas; dali rola por dentro. «/» e «Ctrl + Enter envia» são o texto de
 * apoio do campo (`aria-describedby`; o atalho também no `aria-keyshortcuts`
 * do «Enviar», que é o que ele aciona): uma linha miúda
 * embaixo, à vista só com o campo em foco (no PC). A zona do anexo e o
 * anexo escolhido ficam acima da fileira.
 */
function Compositor({ conversaId, canalTipo, bloqueio, celular, rascunho, aoUsarRascunho, aoEnviar }: {
  conversaId: string; canalTipo: string;
  /** O texto que a Nova conversa trouxe até a conversa já aberta (onda 554, Tarefa 7): entra no campo, sem enviar. */
  rascunho: string | null;
  /** O rascunho entrou no campo: quem o guardou o esquece (voltar à conversa não o escreve de novo). */
  aoUsarRascunho(): void;
  /** No celular não há Ctrl + Enter: a dica do campo não o cita. */
  celular: boolean;
  /** Por que a pessoa não responde aqui (a conversa é de outra): o compositor fica indisponível, com o motivo (P-UI-25). */
  bloqueio: string | null;
  aoEnviar(aviso: string | null): void;
}) {
  const [texto, setTexto] = useState('');
  const [anexando, setAnexando] = useState(false);
  const [anexo, setAnexo] = useState<{ arquivoId: string; nome: string; tamanho: number } | null>(null);
  const [subindo, setSubindo] = useState<number | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [prontas, setProntas] = useState<RespostaPronta[] | null>(null);
  const [marcada, setMarcada] = useState(0);
  const idLista = useId();
  const area = useRef<HTMLTextAreaElement>(null);

  /* O campo cresce com o texto até o teto da folha (`max-height`, ~6
     linhas) e, dali, rola por dentro. */
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    /* Vazio, a altura é a da folha (uma linha): o `scrollHeight` do campo
       vazio mediria o `placeholder` quebrado. */
    if (!texto) { el.style.height = ''; return; }
    el.style.height = 'auto';
    const teto = parseFloat(getComputedStyle(el).maxHeight) || Infinity;
    const borda = el.offsetHeight - el.clientHeight;
    el.style.height = `${Math.min(el.scrollHeight + borda, teto)}px`;
  }, [texto]);

  useEffect(() => { setTexto(''); setAnexo(null); setAnexando(false); setErro(null); }, [conversaId]);
  useEffect(() => {
    if (!rascunho) return;
    setTexto(rascunho);
    aoUsarRascunho();
  }, [rascunho]); // eslint-disable-line react-hooks/exhaustive-deps

  /* O «/» abre a lista: só no começo, e até o primeiro espaço. */
  const busca = /^\/(\S*)$/.exec(texto)?.[1] ?? null;
  useEffect(() => {
    if (busca === null || prontas) return;
    buscarRespostas().then(setProntas).catch(() => setProntas([]));
  }, [busca, prontas]);
  const opcoes = useMemo(() => {
    if (busca === null || !prontas) return [];
    const q = semAcento(busca);
    return prontas.filter((r) => semAcento(r.atalho ?? '').startsWith(q) || semAcento(r.titulo).includes(q)).slice(0, 8);
  }, [busca, prontas]);
  useEffect(() => { setMarcada(0); }, [busca]);
  const listaAberta = busca !== null && opcoes.length > 0;

  const escolher = (r: RespostaPronta) => {
    setTexto(r.texto);
    area.current?.focus();
  };

  const subir = async (f: File | undefined) => {
    if (!f) return;
    setErro(null);
    setSubindo(0);
    try {
      const fd = new FormData();
      fd.append('arquivo', f);
      const r = await enviarFormulario<{ arquivoId: string; nome: string; tamanho: number }>(
        '/comercial/anexos', fd, (p) => setSubindo(p));
      setAnexo(r);
      setAnexando(false);
    } catch (e) {
      setErro(e);
    } finally {
      setSubindo(null);
    }
  };

  const pronto = !bloqueio && ((texto.trim() !== '' && busca === null) || anexo !== null);
  const idBloqueio = `${idLista}-bloqueio`;
  const idDica = `${idLista}-dica`;
  const enviar = async () => {
    if (!pronto || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      const r = await api<{ aviso: string | null }>(`/comercial/conversas/${conversaId}/mensagens`, {
        metodo: 'POST',
        corpo: { ...(texto.trim() ? { texto: texto.trim() } : {}), ...(anexo ? { arquivoId: anexo.arquivoId } : {}) },
      });
      setTexto('');
      setAnexo(null);
      aoEnviar(r?.aviso ?? null);
    } catch (e) {
      setErro(e);
    } finally {
      setEnviando(false);
    }
  };

  const aoTeclar = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (listaAberta) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMarcada((i) => (i + 1) % opcoes.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMarcada((i) => (i - 1 + opcoes.length) % opcoes.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); escolher(opcoes[marcada]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setTexto(''); return; }
    }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void enviar(); }
  };

  const porEmail = canalTipo === 'email' || canalTipo === 'site';
  return (
    <form className={`compositor${bloqueio ? ' bloqueado' : ''}`} onSubmit={(e) => { e.preventDefault(); void enviar(); }}>
      {bloqueio && <p className="compositor-bloqueio" id={idBloqueio}>{bloqueio}</p>}
      {erro ? <ErroDaApi erro={erro} /> : null}
      {anexando && (
        <ZonaDeArquivo rotulo="Anexo" tipos="PDF, imagem, áudio ou documento, até 25 MB"
          desabilitado={subindo !== null} aoEscolher={(fs) => void subir(fs[0])}
          textoVazio={subindo !== null ? `Enviando o anexo… ${Math.round(subindo * 100)} %` : undefined} />
      )}
      {anexo && (
        <div className="compositor-anexo">
          <span>{anexo.nome} <span className="msg-aux">{tamanho(anexo.tamanho)}</span></span>
          <button type="button" className="botao discreto" onClick={() => setAnexo(null)}>Tirar</button>
        </div>
      )}
      <div className="compositor-linha">
        <button type="button" className="botao secundario so-icone compositor-anexar" aria-expanded={anexando}
          aria-label="Anexar um arquivo" title="Anexar um arquivo"
          aria-disabled={bloqueio ? true : undefined} aria-describedby={bloqueio ? idBloqueio : undefined}
          onClick={() => { if (!bloqueio) setAnexando((v) => !v); }}>
          <Icone nome="anexo" />
        </button>
        <div className="compositor-campo">
          <textarea ref={area} value={texto} rows={1} aria-label="Resposta"
            readOnly={!!bloqueio} aria-disabled={bloqueio ? true : undefined}
            aria-describedby={bloqueio ? idBloqueio : idDica}
            placeholder={porEmail ? 'Escreva a resposta (sai por e-mail).' : 'Escreva a resposta.'}
            role="combobox" aria-expanded={listaAberta} aria-controls={idLista} aria-autocomplete="list"
            aria-activedescendant={listaAberta ? `${idLista}-${marcada}` : undefined}
            onChange={(e) => setTexto(e.target.value)} onKeyDown={aoTeclar} />
          {listaAberta && (
            <ul className="respostas-prontas" id={idLista} role="listbox" aria-label="Respostas prontas">
              {opcoes.map((r, i) => (
                <li key={r.id} id={`${idLista}-${i}`} role="option" aria-selected={i === marcada}
                  onMouseDown={(e) => { e.preventDefault(); escolher(r); }}>
                  <strong>{r.titulo}</strong>
                  {r.atalho && <span className="msg-aux">/{r.atalho}</span>}
                  <span className="resposta-previa">{r.texto}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button type="submit" className="botao" aria-disabled={!pronto || enviando}
          aria-describedby={bloqueio ? idBloqueio : undefined} aria-keyshortcuts="Control+Enter">
          {enviando ? 'Enviando…' : 'Enviar'}
        </button>
      </div>
      <p className="compositor-dica" id={idDica}>
        «/» no começo abre as respostas prontas{celular ? '.' : ' · Ctrl + Enter envia.'}
      </p>
    </form>
  );
}

// ── A conversa ───────────────────────────────────────────────────────

type Janela = 'transferir' | 'ligacao' | 'devolver' | 'encerrar' | null;

export function Conversa({ id, eu, diretoria, lateralNaFolha, celular, aoAbrirLateral, aoMudar, aoCarregar, versao = 0 }: {
  id: string;
  /** Muda quando o painel ao lado mexeu na conversa (a oportunidade, o contato): busca de novo. */
  versao?: number;
  eu: string;
  diretoria: boolean;
  /** Abaixo de 1100 px o painel do cliente mora na folha: o cabeçalho ganha o botão. */
  lateralNaFolha: boolean;
  /** No celular a conversa ocupa a tela (o «voltar» é o da trilha) e as ações cabem menos. */
  celular: boolean;
  aoAbrirLateral(): void;
  /** Algo mudou que a lista também mostra (dono, situação, resposta). */
  aoMudar(): void;
  aoCarregar(d: DetalheDaConversa | null): void;
}) {
  const [d, setD] = useState<DetalheDaConversa | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [acaoErro, setAcaoErro] = useState<unknown>(null);
  const [acaoOk, setAcaoOk] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [janela, setJanela] = useState<Janela>(null);
  const [assumindo, setAssumindo] = useState(false);
  /* «Gravar contato» (Tarefa 7) e «Chamar no WhatsApp» (Tarefa 8): as janelas da Parte B. */
  const [gravando, setGravando] = useState(false);
  const [chamando, setChamando] = useState<InicialDaNovaConversa | null>(null);
  /* Onda 555: o cartão de contato que vira «Gravar contato», e o «Trocar o local da obra?». */
  const [novoDoCartao, setNovoDoCartao] = useState<CartaoDaLinha | null>(null);
  const [usandoLocal, setUsandoLocal] = useState<string | null>(null);
  const [trocaDoLocal, setTrocaDoLocal] = useState<{ mensagemId: string; atual: LocalDaObra; novo: LocalDaObra } | null>(null);
  const [erroDaTroca, setErroDaTroca] = useState<unknown>(null);
  const { pode } = useSessao();
  const responde = pode('atendimento.responder');
  const corpo = useRef<HTMLDivElement>(null);
  const ultimaVista = useRef<string | null>(null);
  const pedido = useRef(0);

  /*
   * O rascunho que a Nova conversa traz (onda 554, Tarefa 7): o contato já
   * tinha conversa aberta no canal, e «Enviar» leva até ela com o texto
   * escrito no compositor — nada enviado. Vem no estado da navegação; a
   * conversa o guarda e limpa o estado, para o F5 e o «voltar» não o
   * escreverem de novo, e o esquece assim que o compositor o usa (sair e
   * voltar à conversa não o escreve outra vez).
   */
  const local = useLocation();
  const navegar = useNavigate();
  const [rascunho, setRascunho] = useState<{ id: string; texto: string } | null>(null);
  useEffect(() => {
    const texto = (local.state as { rascunho?: unknown } | null)?.rascunho;
    if (typeof texto !== 'string' || !texto) return;
    setRascunho({ id, texto });
    navegar(`${local.pathname}${local.search}`, { replace: true, state: null });
  }, [id, local.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const buscar = useCallback(async () => {
    const n = ++pedido.current;
    try {
      const r = await api<DetalheDaConversa>(`/comercial/conversas/${id}`);
      if (n !== pedido.current) return;
      setD(r);
      setErro(null);
      aoCarregar(r);
    } catch (e) {
      if (n !== pedido.current) return;
      setErro(e);
      aoCarregar(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setD(null); setErro(null); setAcaoErro(null); setAcaoOk(null); setAviso(null); setJanela(null);
    setGravando(false); setChamando(null); setNovoDoCartao(null); setTrocaDoLocal(null); setUsandoLocal(null);
    ultimaVista.current = null;
    void buscar();
  }, [buscar]);
  /* O painel mexeu na conversa: busca de novo, sem limpar a tela. */
  const versaoVista = useRef(versao);
  useEffect(() => {
    if (versaoVista.current === versao) return;
    versaoVista.current = versao;
    void buscar();
  }, [versao, buscar]);

  /* Ao vivo: o aviso só traz ids — busca de novo (e o «recarregar» também).
     Uma rajada de avisos (a mensagem e a entrega dela) vira uma busca só. */
  const adiada = useRef<number | null>(null);
  const tempoReal = useTempoReal([`comercial.conversa.${id}`], () => {
    if (adiada.current !== null) return;
    adiada.current = window.setTimeout(() => { adiada.current = null; void buscar(); }, 150);
  });
  useEffect(() => () => { if (adiada.current !== null) window.clearTimeout(adiada.current); }, []);

  /* A linha do tempo desce até a mensagem mais nova quando chega uma — o
     painel dela rola, e não a página (no celular, onde o painel não rola,
     quem desce é a página). */
  useEffect(() => {
    const ultima = d?.linhaDoTempo.at(-1)?.id ?? null;
    if (!ultima || ultima === ultimaVista.current) return;
    ultimaVista.current = ultima;
    const el = corpo.current;
    if (!el) return;
    if (el.scrollHeight > el.clientHeight + 1) el.scrollTop = el.scrollHeight;
    else if (celular) window.scrollTo({ top: document.documentElement.scrollHeight });
  }, [d, celular]);

  /*
   * Quem está no fim da conversa continua no fim quando o compositor cresce
   * (o campo vai até ~6 linhas e a dica aparece com o foco) ou a janela muda
   * de altura: a linha do tempo encolhe por baixo, e sem isto a mensagem que
   * se está respondendo sairia da vista (I-2 da revisão de 23/10/2026). Quem
   * rolou para cima para ler fica onde está. No celular quem rola é a
   * página, e o compositor preso no pé já fica sobre ela.
   */
  const temConversa = d !== null;
  useEffect(() => {
    const el = corpo.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const noFim = () => el.scrollHeight - el.scrollTop - el.clientHeight <= 8;
    let estavaNoFim = noFim();
    let altura = el.clientHeight;
    /* O `scroll` chega antes do `ResizeObserver` no mesmo quadro: com a
       altura já mudada, quem decide é o observador (senão o encolher
       pareceria «saiu do fim»). */
    const aoRolar = () => { if (el.clientHeight === altura) estavaNoFim = noFim(); };
    const aoMudar = () => {
      if (estavaNoFim && el.scrollHeight > el.clientHeight + 1) el.scrollTop = el.scrollHeight;
      altura = el.clientHeight;
      estavaNoFim = noFim();
    };
    el.addEventListener('scroll', aoRolar, { passive: true });
    const observador = new ResizeObserver(aoMudar);
    observador.observe(el);
    return () => { el.removeEventListener('scroll', aoRolar); observador.disconnect(); };
  }, [temConversa]);

  const depoisDaAcao = (ok: string | null) => {
    setAcaoErro(null);
    setAcaoOk(ok);
    setJanela(null);
    void buscar();
    aoMudar();
  };

  const assumir = async () => {
    if (assumindo) return;
    setAssumindo(true);
    setAcaoOk(null);
    try {
      await api(`/comercial/conversas/${id}/assumir`, { metodo: 'POST' });
      depoisDaAcao('A conversa é sua agora.');
    } catch (e) {
      setAcaoErro(e);
      void buscar();
    } finally {
      setAssumindo(false);
    }
  };

  const tentarDeNovo = async (saidaId: string) => {
    setAcaoOk(null);
    try {
      await api(`/comercial/saidas/${saidaId}/reenviar`, { metodo: 'POST' });
      setAcaoErro(null);
      void buscar();
    } catch (e) {
      setAcaoErro(e);
      void buscar();
    }
  };

  /* Marcar ou desmarcar a mensagem como comercial (Tarefa 16b): a resposta
     nasce na barra de respostas da conversa (P-UI-26), e a conversa busca de
     novo — as outras telas abertas recebem o aviso pelo tempo real. */
  const marcar = async (mensagemId: string, comercial: boolean) => {
    setAcaoOk(null);
    try {
      await api(`/comercial/conversas/${id}/mensagens/${mensagemId}/comercial`, { metodo: 'POST', corpo: { comercial } });
      setAcaoErro(null);
      setAcaoOk(comercial
        ? 'A mensagem ficou marcada como comercial: quem não tem o Comercial vê só o lugar dela.'
        : 'A mensagem deixou de ser comercial: quem vê a conversa vê a mensagem inteira.');
      void buscar();
      aoMudar();
    } catch (e) {
      setAcaoErro(e);
      void buscar();
    }
  };

  /*
   * «Usar como local da obra» (§3.1.1): grava a coordenada (e o endereço, se o da oportunidade está vazio).
   * Já havendo outro local, a API responde 409 com o atual e o novo, e a janela pergunta «Trocar o local da
   * obra?»; confirmada, vai de novo com `trocar`. As outras recusas (sem oportunidade, a de outra pessoa)
   * aparecem como vêm, na barra de respostas da conversa.
   */
  const usarComoLocal = async (mensagemId: string, trocar = false) => {
    if (usandoLocal) return;
    setUsandoLocal(mensagemId);
    setAcaoOk(null);
    setErroDaTroca(null);
    try {
      await api(`/comercial/conversas/${id}/mensagens/${mensagemId}/local-da-obra`, {
        metodo: 'POST', corpo: trocar ? { trocar: true } : {},
      });
      setTrocaDoLocal(null);
      setAcaoErro(null);
      setAcaoOk('O local da obra da oportunidade agora é o desta localização.');
      void buscar();
      aoMudar();
    } catch (e) {
      const corpo = e instanceof ErroApi ? e.corpo as { atual?: LocalDaObra; novo?: LocalDaObra } | undefined : undefined;
      if (!trocar && e instanceof ErroApi && e.status === 409 && corpo?.atual && corpo.novo) {
        setAcaoErro(null);
        setTrocaDoLocal({ mensagemId, atual: corpo.atual, novo: corpo.novo });
      } else if (trocar) {
        setErroDaTroca(e);
      } else {
        setAcaoErro(e);
      }
    } finally {
      setUsandoLocal(null);
    }
  };

  /* A divisória é uma só, no corte que a API manda: depois da última mensagem do histórico que está na
     própria linha do tempo (histórico dentro da conversa, ou conversa do histórico) ou, se o histórico vem
     de outras conversas, depois do bloco dele. */
  const { antes, depois, divisoriaNoFim } = useMemo(() => {
    const l = d?.linhaDoTempo ?? [];
    const k = l.map((m) => m.historico === true).lastIndexOf(true);
    return {
      antes: agruparPorDia(l.slice(0, k + 1)), depois: agruparPorDia(l.slice(k + 1)), divisoriaNoFim: k >= 0,
    };
  }, [d]);
  const gruposDoHistorico = useMemo(() => agruparPorDia(d?.historico?.mensagens ?? []), [d]);

  if (erro && !d) {
    return (
      <section className="conversa caixa-coluna">
        <div className="conversa-corpo"><ErroDaApi erro={erro} /></div>
      </section>
    );
  }
  if (!d) {
    return (
      <section className="conversa caixa-coluna" aria-busy="true">
        <div className="conversa-corpo"><Carregando texto="Carregando a conversa" /></div>
      </section>
    );
  }

  const c = d.conversa;
  const encerrada = c.situacao === 'encerrada';
  const minha = c.dono.tipo === 'pessoa' && c.dono.id === eu;
  const deOutra = c.dono.tipo === 'pessoa' && c.dono.id !== eu && c.assumidaEm !== null;
  const sit = degrauDe(SITUACAO_DO_ATENDIMENTO, situacaoDoAtendimento(c));
  const relogio = [semRespostaHa(c.minutosSemResposta), passaA(c.substituto, c.prazoSubstituto)].filter(Boolean);
  const telefone = c.canal.tipo === 'telefone';
  const contatoLegivel = nomeDoContato(c.contato.nome);
  const podeDevolver = c.tipo === 'cliente' && c.canal.agenteAtivo && c.dono.tipo !== 'agente' && !c.naoReceberEm;
  const corte = d.corteDoHistorico ?? d.historico?.ate ?? null;
  const divisoria = corte && (divisoriaNoFim || d.historico) ? (
    <li key="divisoria-do-historico" className="conversa-dia conversa-historico">
      <h3 className="conversa-dia-rotulo">Importado do WhatsApp (até {data(corte)})</h3>
    </li>
  ) : null;
  const bloqueio = deOutra && !diretoria
    ? `${c.dono.nome} está com esta conversa. Para responder, peça a transferência a quem está com ela.`
    : null;
  const naoGravado = d.contato.salvo === false;
  const perfil = naoGravado ? d.contato.whatsappPerfil : null;
  /* A primeira mensagem de quem abriu a conversa (§3.2). */
  const primeiraDaEquipe = c.iniciadaPor
    ? d.linhaDoTempo.find((m) => m.direcao === 'enviada' && m.autor?.id === c.iniciadaPor!.id)?.id ?? null : null;
  /*
   * «Chamar no WhatsApp» (§3.4): da conversa que não é de WhatsApp (a de
   * telefone, sobretudo), a Nova conversa já com o contato, ligada à mesma
   * oportunidade — a aberta desta conversa, para quem edita o Comercial.
   */
  const opAberta = d.oportunidade && !['ganha', 'perdida', 'descartada'].includes(d.oportunidade.estagio)
    ? d.oportunidade.id : null;
  const chamaNoWhatsapp = responde && c.canal.tipo !== 'whatsapp' && c.tipo === 'cliente';
  const chamarNoWhatsapp = () => setChamando(chamarOContato(d.contato, pode('crm.editar') ? opAberta : null));
  const numeroDoContato = d.contato.whatsapp ? telefoneLegivel(d.contato.whatsapp)
    : d.contato.telefone ? telefoneDigitadoLegivel(d.contato.telefone) : null;
  /* O cabeçalho (§3.1): o cliente do contato, e o telefone (no e-mail, o endereço) — o número que já é o
     título (o contato sem nome) não se repete. */
  const clientesDoContato = d.clientes.map((k) => k.nome).join(', ');
  const enderecoDoContato = (() => {
    const v = c.canal.tipo === 'email' ? d.contato.email ?? numeroDoContato : numeroDoContato ?? d.contato.email;
    return v && v !== contatoLegivel ? v : null;
  })();
  /*
   * As ações das mídias novas (onda 555). «Usar como local da obra» só na linha do tempo desta conversa (o
   * bloco do histórico de outras conversas não grava aqui), com a oportunidade ligada, para quem edita o
   * Comercial; o cartão grava o contato e abre a Nova conversa para quem responde.
   */
  const acoesDoCartao: AcoesDaMidia = responde ? {
    aoGravarContato: (k) => setNovoDoCartao(k),
    aoNovaConversa: (k) => { if (k.telefones[0]) setChamando({ numero: k.telefones[0] }); },
    aoAbrirContato: (contatoId) => navegar(`/comercial/contato/${contatoId}`),
  } : { aoAbrirContato: (contatoId) => navegar(`/comercial/contato/${contatoId}`) };
  const acoesDaConversa: AcoesDaMidia = {
    ...acoesDoCartao,
    ...(d.oportunidade && pode('crm.editar')
      ? { aoUsarComoLocal: (mid: string) => void usarComoLocal(mid), usandoLocal } : {}),
  };
  /* Um dia da linha do tempo — do histórico, de antes ou de depois da divisória (onda 553) —, com o que a
     Parte B pôs em cada mensagem: «iniciou a conversa», registrar a ligação, chamar no WhatsApp. */
  const dia = (g: { dia: string; itens: MensagemDaLinha[] }, chave: string, acoes: AcoesDaMidia = acoesDaConversa) => (
    <li key={chave} className="conversa-dia">
      <h3 className="conversa-dia-rotulo">{g.dia}</h3>
      <ol className="conversa-dia-itens">
        {g.itens.map((m) => (
          <Mensagem key={m.id} m={m} contato={contatoLegivel} aoTentar={(s) => void tentarDeNovo(s)}
            aoMarcar={d.veComercial ? (mid, com) => void marcar(mid, com) : undefined}
            iniciou={m.id === primeiraDaEquipe}
            aoRegistrarLigacao={!encerrada && responde ? () => setJanela('ligacao') : undefined}
            chamar={chamaNoWhatsapp && m.tipo === 'ligacao' ? { numero: numeroDoContato, aoChamar: chamarNoWhatsapp } : undefined}
            acoes={acoes} />
        ))}
      </ol>
    </li>
  );

  return (
    <section className="conversa caixa-coluna" aria-labelledby={`conversa-${c.id}`}>
      {/* O cabeçalho compacto (P-LST-15, quadro A1): na 1ª linha o nome, o cliente e
          as ações; embaixo, os selos, o canal, a rota, o telefone, com quem está e o relógio. A barra
          de ações que havia abaixo do cabeçalho saiu — o espaço é da linha
          do tempo. */}
      <header className="conversa-cabeca">
        <div className="conversa-titulo">
          {/* A foto do perfil do WhatsApp, ou as iniciais (onda 555, §3.1). */}
          <FotoDoContato contatoId={d.contato.id} nome={contatoLegivel} temFoto={d.contato.temFoto === true} tamanho="p" />
          <div className="conversa-titulo-texto">
            {/* 1ª linha (quadro A1): o nome e o cliente. O nome nunca quebra; sem espaço, o cliente
                encurta com «…» e mostra o nome inteiro ao passar o mouse (P-LST-15). */}
            <div className="conversa-nome">
              <h2 id={`conversa-${c.id}`} title={contatoLegivel}>
                {contatoLegivel}
              </h2>
              {clientesDoContato && (
                <span className="conversa-cliente" title={clientesDoContato}>· {clientesDoContato}</span>
              )}
            </div>
          </div>
        </div>
        <div className="conversa-cabeca-acoes">
          {chamaNoWhatsapp && (
            <button type="button" className="botao conversa-chamar" onClick={chamarNoWhatsapp}>
              Chamar no WhatsApp
            </button>
          )}
          {!encerrada && (
            <div className="conversa-acoes" role="toolbar" aria-label="Ações da conversa">
              {!(minha && c.assumidaEm) && (
                <button type="button" className="botao secundario" aria-disabled={assumindo} onClick={() => void assumir()}>
                  Assumir
                </button>
              )}
              <button type="button" className="botao secundario" onClick={() => setJanela('transferir')}>Transferir</button>
              {!celular && (
                <button type="button" className="botao secundario" onClick={() => setJanela('ligacao')}>Registrar ligação</button>
              )}
              {/* O que se usa menos mora no «⋯» da casa (P-UI-33), como na OS. */}
              <MenuDaOs rotulo="Mais ações da conversa">
                {/* O contato que chegou sozinho: «Gravar contato» aqui e no painel — o cabeçalho fica numa
                    fileira (P-LST-15; correção 1 da Tarefa 8). */}
                {naoGravado && responde && (
                  <button type="button" className="item-menu" onClick={() => setGravando(true)}>{GRAVAR_CONTATO}</button>
                )}
                {celular && (
                  <button type="button" className="item-menu" onClick={() => setJanela('ligacao')}>Registrar ligação</button>
                )}
                {podeDevolver && (
                  <button type="button" className="item-menu" onClick={() => setJanela('devolver')}>Devolver ao agente</button>
                )}
                <button type="button" className="item-menu" onClick={() => setJanela('encerrar')}>Encerrar a conversa</button>
              </MenuDaOs>
            </div>
          )}
          {lateralNaFolha && (
            <button type="button" className="botao secundario conversa-lateral" onClick={aoAbrirLateral}>
              Cliente e oportunidade
            </button>
          )}
        </div>
        <div className="conversa-detalhe">
          {/* Quem chegou sozinho (§3.2): «~perfil» em cinza e o selo, na linha de baixo do cabeçalho, que
              tem a largura toda — no título, a 1280 px com o painel ao lado, eles quebravam a fileira
              (P-LST-15; correção 1 da Tarefa 8). */}
          {naoGravado && (
            <p className="conversa-nao-gravado">
              {perfil && <span className="conversa-perfil">~{perfil}</span>}
              <Selo tom="atencao">Não gravado</Selo>
            </p>
          )}
          {/* Os selos descem para a 2ª linha: a 1280 px, com o painel ao lado, a 1ª é do nome, do cliente e das
              ações (P-LST-15). */}
          <div className="conversa-selos">
            {d.contato.contaComercial && <Selo tom="andamento">Conta comercial</Selo>}
            {c.prioridade && <Selo tom="critico">Prioridade</Selo>}
            <SeloDaEscada degrau={sit} />
          </div>
          {/* 2ª linha (quadro A1): o canal, o telefone e com quem está — «WhatsApp central · +55 (47) … · com Humberto». */}
          <p className="conversa-sub">
            {c.canal.nome}
            {c.rota ? ` · ${c.rota.nome}` : ''}
            {enderecoDoContato && <>{' · '}<span className="conversa-numero">{enderecoDoContato}</span></>}
            {' · '}{minuscula(comQuem(c))}
          </p>
          {relogio.length > 0 && <p className="conversa-relogio">{relogio.join(' · ')}</p>}
        </div>
      </header>
      <div className="conversa-resposta">
        <RespostaDaAcao erro={acaoErro} ok={acaoOk} />
        {aviso && <Aviso tipo="info">{aviso}</Aviso>}
        <AtualizacaoPausada estado={tempoReal} />
      </div>

      <div className="conversa-corpo" ref={corpo}>
        {c.emailAssunto && <p className="conversa-assunto">Assunto: <strong>{c.emailAssunto}</strong></p>}
        <ol className="conversa-linha" aria-label="Linha do tempo da conversa">
          {gruposDoHistorico.map((g) => dia(g, `h-${g.dia}`, acoesDoCartao))}
          {!divisoriaNoFim && divisoria}
          {antes.map((g) => dia(g, `a-${g.dia}`))}
          {divisoriaNoFim && divisoria}
          {depois.map((g) => dia(g, g.dia))}
        </ol>
      </div>

      {encerrada ? (
        <p className="conversa-fechada">
          Esta conversa foi encerrada{c.encerradaEm ? ` em ${data(c.encerradaEm)}` : ''}. Se o cliente escrever de novo,
          a conversa nova aparece na caixa de entrada.
        </p>
      ) : telefone ? (
        <p className="conversa-fechada">
          O que se fala ao telefone se registra como ligação: use «Registrar ligação», na barra acima.
        </p>
      ) : (
        <>
          {/* O copiloto (Tarefa 24): a sugestão do agente, acima do compositor. */}
          {d.sugestao && (
            <SugestaoDoAgente sugestao={d.sugestao} bloqueio={bloqueio}
              aoResolver={(r) => { setAviso(r.aviso); setAcaoErro(null); setAcaoOk(r.ok); void buscar(); aoMudar(); }} />
          )}
          <Compositor conversaId={c.id} canalTipo={c.canal.tipo} celular={celular}
            bloqueio={bloqueio} rascunho={rascunho?.id === c.id ? rascunho.texto : null}
            aoUsarRascunho={() => setRascunho(null)}
            aoEnviar={(a) => { setAviso(a); setAcaoErro(null); setAcaoOk(null); void buscar(); aoMudar(); }} />
        </>
      )}

      <JanelaDaLigacao aberta={janela === 'ligacao'} conversaId={c.id} contato={contatoLegivel}
        contatoDaConversa={d.contato}
        aoFechar={() => setJanela(null)} aoGravar={(r) => {
          depoisDaAcao(r.oportunidadeId ? 'A ligação ficou registrada na conversa, com a oportunidade.'
            : 'A ligação ficou registrada na conversa.');
          if (r.chamar) setChamando(r.chamar);
        }} />
      {gravando && (
        <JanelaDoContato modo="salvar" contato={d.contato} aoFechar={() => setGravando(false)}
          aoGravar={() => { setGravando(false); depoisDaAcao('Contato gravado.'); }} />
      )}
      {novoDoCartao && (
        <JanelaDoContato modo="novo"
          contato={{ id: '', nome: novoDoCartao.nome ?? '', whatsapp: novoDoCartao.telefones[0] ?? null }}
          aoFechar={() => setNovoDoCartao(null)}
          aoGravar={() => { setNovoDoCartao(null); depoisDaAcao('Contato gravado.'); }} />
      )}
      <JanelaDoLocalDaObra aberta={trocaDoLocal !== null} contato={contatoLegivel}
        atual={trocaDoLocal?.atual ?? null} novo={trocaDoLocal?.novo ?? null}
        ocupado={usandoLocal !== null} erro={erroDaTroca}
        aoFechar={() => { setTrocaDoLocal(null); setErroDaTroca(null); }}
        aoTrocar={() => { if (trocaDoLocal) void usarComoLocal(trocaDoLocal.mensagemId, true); }} />
      <JanelaNovaConversa aberta={chamando !== null} inicial={chamando ?? undefined} aoFechar={() => setChamando(null)}
        aoAbrir={(conversaId, rascunho) => {
          setChamando(null);
          aoMudar();
          navegar(`/comercial/conversa/${conversaId}`, rascunho ? { state: { rascunho } } : undefined);
        }} />
      <JanelaDeTransferir aberta={janela === 'transferir'} conversaId={c.id} contato={contatoLegivel}
        aoFechar={() => setJanela(null)}
        aoTransferir={(nome) => depoisDaAcao(nome ? `A conversa foi para ${nome}.` : 'A conversa foi transferida.')} />
      <JanelaDeDevolver aberta={janela === 'devolver'} conversaId={c.id} contato={contatoLegivel}
        aoFechar={() => setJanela(null)} aoDevolver={() => depoisDaAcao('A conversa voltou ao agente.')} />
      <JanelaDeEncerrar aberta={janela === 'encerrar'} conversaId={c.id} contato={contatoLegivel}
        aoFechar={() => setJanela(null)} aoEncerrar={() => depoisDaAcao('A conversa foi encerrada.')} />
    </section>
  );
}
